import { Stage, Layer, Circle, Line } from 'react-konva'
import { useState, useEffect, useRef } from 'react'
import { useSocketEvent } from '../../helpers/useSocket'
import useImage from 'use-image'
import classNames from 'classnames'
import colors from '../../config/colors.json'

export function LiveGameMinimap({ width, height, leftColor, rightColor }) {
	const [minimapData, setMinimapData] = useState([])
	const [whiteSheepIcon] = useImage('/minimap/sheep.png')
	const [blackSheepIcon] = useImage('/minimap/sheep.png')
	const [brownSheepIcon] = useImage('/minimap/sheep.png')
	const [pinkSheepIcon] = useImage('/minimap/sheep.png')
	const [showState, setShowState] = useState(false)

	const [shadowBlur, setShadowBlur] = useState(0)
	const frameRef = useRef(0)
	const startRef = useRef(0)

	function getSheepIcon(pbgid) {
		let sheepIcon = whiteSheepIcon
		if (pbgid === 9004501) {
			sheepIcon = blackSheepIcon
		}
		if (pbgid === 9004502) {
			sheepIcon = brownSheepIcon
		}
		if (pbgid === 9004503) {
			sheepIcon = pinkSheepIcon
		}
		return sheepIcon
	}

	useSocketEvent('updateMinimapData', (newdata) => {
		//console.log(`Received updateConfig`, JSON.stringify(newdata));
		setMinimapData(newdata)
	})

	useSocketEvent(`toggleMinimap`, (newData) => {
		//console.log("toggled minimap")
		setShowState(newData)
	})

	//Made by ChatGPT 5.0
	//Need to rotate the map 135 degrees, since there is some weird conversion but I CBA looking into it
	function convertAtoB(xA, yA) {
		const rangeA = 407 // from -203.5 to +203.5
		const rangeB = 242 // size of system B

		// Normalize A’s coordinates to [0, 1]
		const normX = (xA + 203.5) / rangeA
		const normY = (yA + 203.5) / rangeA

		// Scale to B
		const scaledX = normX * rangeB
		const scaledY = normY * rangeB

		// Flip horizontally to account for top-right origin in B
		const xB = rangeB - scaledX
		const yB = scaledY

		return { xB, yB }
	}

	useEffect(() => {
		const min = 10
		const max = 20
		const duration = 3000

		const amplitude = (max - min) / 2
		const center = min + amplitude

		const animate = (timestamp) => {
			if (!startRef.current) {
				startRef.current = timestamp
			}

			const elapsed = timestamp - startRef.current
			const t = (elapsed % duration) / duration

			const sine = Math.sin(t * 2 * Math.PI)
			const blur = center + amplitude * sine

			setShadowBlur(blur)

			frameRef.current = requestAnimationFrame(animate)
		}

		frameRef.current = requestAnimationFrame(animate)

		return () => {
			cancelAnimationFrame(frameRef.current)
		}
	}, [])

	return (
		<Stage
			className={classNames(
				'liveGameOverlay__minimap',
				showState && 'liveGameOverlay__minimapShow',
			)}
			width={width}
			height={height}
			style={{
				border: 'solid',
				borderWidth: '1px',
				width: width,
				borderColor: 'transparent',
			}}
		>
			<Layer>
				{/* <Line points={[0,0,0,242,242,242,242,0]} stroke='red' strokeWidth={2} closed/> */}
				{pinkSheepIcon &&
					minimapData?.neutralSheepPositions?.map((sheep, i) => {
						let sheepIcon = getSheepIcon(sheep.pbg_id)
						return (
							<Circle
								key={'sheep' + i}
								x={
									convertAtoB(
										sheep.position.x,
										sheep.position.z,
									).xB - 15
								}
								y={
									convertAtoB(
										sheep.position.x,
										sheep.position.z,
									).yB + 15
								}
								radius={40}
								fillPatternImage={sheepIcon}
								fillPatternRepeat={'no-repeat'}
								fillPatternOffsetX={1}
								fillPatternOffsetY={1}
								fillPatternScaleX={0.03}
								fillPatternScaleY={0.03}
								fillPatternRotation={-135}
								shadowColor="#e784a3"
								shadowBlur={
									sheep.pbg_id === 9004503 ? shadowBlur : null
								}
								shadowOpacity={0.9}
								shadowOffset={{ x: 0, y: 0 }} // centered glow
							/>
						)
					})}

				{minimapData?.unitTrackers?.Scout?.map((unit, i) => {
					let strokeColorStart =
						unit.owner === 0
							? colors[leftColor]?.mainColorAlpha
							: colors[rightColor]?.mainColorAlpha
					let strokeColorEnd =
						unit.owner === 0
							? colors[leftColor]?.mainColor
							: colors[rightColor]?.mainColor

					strokeColorStart = strokeColorStart.replace('0.1)', '0.4)')

					let positions = unit?.positions.map((position, u) => {
						let pos = convertAtoB(position.x, position.z)
						return [pos.xB, pos.yB]
					})

					// compute flat points and gradient endpoints
					let points = positions.flat(1)
					let startPoint =
						positions && positions.length
							? { x: positions[0][0], y: positions[0][1] }
							: { x: 0, y: 0 }
					let endPoint =
						positions && positions.length
							? {
									x: positions[positions.length - 1][0],
									y: positions[positions.length - 1][1],
								}
							: { x: 0, y: 0 }

					return (
						<>
							<Line
								key={'scout_bg' + i}
								points={points}
								strokeWidth={4}
								strokeEnabled={true}
								tension={0.1}
								bezier
								strokeLinearGradientStartPoint={startPoint}
								strokeLinearGradientEndPoint={endPoint}
								strokeLinearGradientColorStops={[
									0,
									'rgba(0, 0, 0, 0.1)',
									1,
									'black',
								]}
							/>
							<Line
								key={'scout' + i}
								points={points}
								strokeWidth={2.5}
								tension={0.1}
								bezier
								// add a simple red->yellow linear gradient along the line
								strokeLinearGradientStartPoint={startPoint}
								strokeLinearGradientEndPoint={endPoint}
								strokeLinearGradientColorStops={[
									0,
									strokeColorStart,
									1,
									strokeColorEnd,
								]}
							/>
						</>
					)
				})}
			</Layer>
		</Stage>
	)
}
